import React, { useMemo, useRef, Fragment } from "react";
import { Link, NavLink, useSearchParams } from "react-router-dom";
import classNames from "classnames";
import { Swiper, SwiperSlide } from "swiper/react";
import useBreakpoint from "../hooks/useBreakpoint";

const isActive = (link, params, diffParams, name, currentTab, setCurrentTab) => {
  if (setCurrentTab) {
    return name === currentTab;
  }
  const linkParams = new URL(link, window.location.origin).searchParams;
  for (const key of diffParams) {
    if (params.get(key) !== linkParams.get(key)) {
      return false;
    }
  }
  return true;
};

const ConditionalSwiper = ({ children, breakpoint, ...options }) => {
  const showSwiper = useBreakpoint(breakpoint);

  return showSwiper ? (
    <Swiper {...options}>
      {React.Children.map(
        children,
        (child, i) => child && <SwiperSlide key={i}>{child}</SwiperSlide>
      ).filter(Boolean)}
    </Swiper>
  ) : (
    children
  );
};

const Tabs = ({ tabs, className, currentTab, setCurrentTab, count = false, hideEmpty = false }) => {
  const containerRef = useRef();

  const diffParams = useMemo(() => {
    if (!setCurrentTab) {
      const diffByQuery =
        [
          ...new Set(
            tabs.map((tab) => {
              const url = new URL(tab.link, window.location.origin);
              return url.origin + url.pathname;
            })
          ),
        ].length < tabs.length;
      if (diffByQuery) {
        const setParams = tabs.reduce((res, tab) => {
          const params = new URL(tab.link, window.location.origin).searchParams;
          res.add(...params.keys());
          return res;
        }, new Set());
        return [...setParams].filter(Boolean);
      }
      return null;
    }
  }, [tabs, setCurrentTab]);

  const [params] = useSearchParams();
  const mappedTabs = useMemo(() => {
    const mapped =
      diffParams || setCurrentTab
        ? tabs.map((tab) => ({
            ...tab,
            active: isActive(tab.link, params, diffParams, tab.name, currentTab, setCurrentTab),
          }))
        : tabs;
    return count && hideEmpty ? mapped.filter((tab) => tab.count !== 0) : mapped;
  }, [tabs, params, diffParams, currentTab, setCurrentTab, count, hideEmpty]);

  return (
    <div className={classNames("tabs", className)} role="navigation">
      <div className="tabs__container" ref={containerRef}>
        <ConditionalSwiper breakpoint={767} slidesPerView="auto" spaceBetween={10}>
          {mappedTabs.map((tab) => (
            <Fragment key={tab.link ? tab.link : tab.link === "" ? "" : tab.name}>
              {setCurrentTab ? (
                <button
                  name={tab.name}
                  className={classNames("tabs__tab button small", {
                    "primary active": tab.active,
                    "secondary light": !tab.active,
                  })}
                  onClick={(e) => setCurrentTab(e.currentTarget.name)}
                  disabled={tab.count === 0}
                >
                  <span className="tabs__tab-label">{tab.label}</span>
                  {count && (
                    <span
                      className={classNames(
                        "tabs__tab-count",
                        isNaN(tab.count) && "tabs__tab-count--nan"
                      )}
                    >
                      {!isNaN(tab.count) ? tab.count : ""}
                    </span>
                  )}
                </button>
              ) : diffParams ? (
                <Link
                  to={tab.link}
                  className={classNames("tabs__tab button  small", {
                    "primary active": tab.active,
                    "secondary light": !tab.active,
                  })}
                >
                  <span className="tabs__tab-label">{tab.label}</span>
                  {count && (
                    <span
                      className={classNames(
                        "tabs__tab-count",
                        isNaN(tab.count) && "tabs__tab-count--nan"
                      )}
                    >
                      {!isNaN(tab.count) ? tab.count : ""}
                    </span>
                  )}
                </Link>
              ) : (
                <NavLink
                  to={tab.link}
                  end={!!tab.end}
                  className={classNames("tabs__tab button small", {
                    "primary active": tab.link === window.location.pathname,
                    "secondary light": tab.link !== window.location.pathname,
                  })}
                >
                  <span className="tabs__tab-label">{tab.label}</span>
                  {count && (
                    <span
                      className={classNames(
                        "tabs__tab-count",
                        isNaN(tab.count) && "tabs__tab-count--nan"
                      )}
                    >
                      {!isNaN(tab.count) ? tab.count : ""}
                    </span>
                  )}
                </NavLink>
              )}
            </Fragment>
          ))}
        </ConditionalSwiper>
      </div>
    </div>
  );
};

export default Tabs;
